/* =============================================================================
   VIEWS / CUSTOMER-TABLE — Gast-Seite am Tisch (S244 Cut B)
   =============================================================================
   Route `#tisch/<token>`: der Gast scannt den QR-Aufsteller, legt Gerichte in
   den GEMEINSAMEN Warenkorb der Tisch-Belegung und einer sendet ab.

   Drei Dinge, die diese Datei bewusst NICHT tut:

   1. **Sie rechnet keine Preise.** Der Picker zeigt an, der Server rechnet
      (`price_order_line`). Ein anonymer Gast könnte sonst den Burger auf einen
      Cent setzen, und der Stripe-Guard verglichen später nur Client-Summe gegen
      Client-Summe.
   2. **Sie hält keinen eigenen Warenkorb.** `initMenuPicker` läuft mit dem
      `onLineConfirmed`-Hook: jede bestätigte Zeile geht sofort an den Server,
      der lokale Picker-Warenkorb bleibt leer. Zwei Warenkörbe nebeneinander
      wären genau das Dual-System, das der Cut vermeiden soll.
   3. **Sie speichert nichts Lokales ausser Identität.** In `localStorage` liegen
      nur `guest_key` (anonyme UUID je Handy) und der freiwillige Vorname —
      keine Bestelldaten. Was bestellt wurde, steht auf dem Server.

   Zustand kommt aus `get_table_session` (Poll alle 4 s, solange die Seite
   sichtbar ist). Das ist kein Realtime-Ersatz aus Bequemlichkeit: anonyme
   Clients haben keinen RLS-SELECT auf `orders`, also gibt es für sie kein
   `postgres_changes`. Dasselbe Muster wie die Live-Tracking-Seite.
============================================================================ */

import { htmlEscape, fmtEUR, showToast } from '../utils/helpers.js';
import { initMenuPicker } from './menu-picker.js';
import {
  renderCartSheet, cartBarText, cartSummary, submitConfirmBody, nextRoundNo,
  myGuestNote, foreignGuestNotes, noteCounterText
} from './customer-table-cart.js';
// S254 — der Gast sieht denselben Bon wie Archiv, PDF, Druck und Mail
// (Jonas: „Denselben Bon wie überall sonst"). Der Baustein liegt bewusst in
// einem eigenen schlanken Modul, damit die Gast-Seite nicht den Cockpit-Pfad
// mit Beleg-Mail, PDF-Anhang und Bon-Archiv mitzieht.
import { buildStandaloneReceiptHtml } from './admin/receipt-standalone.js';
import { DEFAULT_LOGO_URL } from '../core/branding.js';

const POLL_MS = 4000;
const GUEST_KEY_PREFIX = 'gg_table_guest_';
const GUEST_NAME_PREFIX = 'gg_table_name_';
const UI_PREFIX = 'gg_table_ui_';

let _token = null;
let _guestKey = null;
let _guestName = '';
let _session = null;
let _pollTimer = null;
let _pollToken = 0;          // Race-Guard: alte Antworten verwerfen
let _failStreak = 0;
let _sheetOpen = false;
let _sheetMode = 'cart';     // 'cart' | 'pay' | 'receipt' (S246)
let _payOptions = null;      // { mine_cents, all_cents } — nur solange das Sheet offen ist
let _receiptView = null;     // guest_receipt_view-Antwort
let _receiptHtml = null;     // fertiges Bon-Dokument (S254) — einmal je Beleg
let _receiptHtmlFor = null;  // zu welcher receipt_id gehört `_receiptHtml`?
let _menuMounted = false;
let _visibilityBound = false;

/**
 * S251 — was der Gast an DIESEM Handy auf- und zugeklappt hat.
 *
 * `menu` ist absichtlich dreiwertig: `null` heisst „noch nie angefasst" und
 * lässt die Automatik gelten (vor der ersten Runde offen, danach zu). Erst ein
 * Tipp auf „Karte"/„Nachbestellen" schreibt `true`/`false` — sonst würde die
 * Automatik die Entscheidung des Gastes bei jedem Poll wieder überschreiben.
 *
 * Liegt in `localStorage`, damit ein Reload (oder der Rückweg von Stripe) den
 * Zustand nicht vergisst. Es sind Ansichts-Vorlieben, keine Bestelldaten — die
 * Regel „nichts Lokales ausser Identität" aus dem Datei-Kopf bleibt gewahrt.
 */
let _ui = { menu: null, groups: [], lines: [] };

/* S253 — Küchen-Hinweis: was zuletzt an den Server ging, und der laufende
   Verzögerungs-Timer. `_noteSaved` verhindert, dass Debounce und Blur denselben
   Text zweimal schicken (der Rate-Zähler liegt bei 30 Schreibvorgängen je
   Minute, und ein Doppelschlag je Tastendruck-Pause käme dem nahe). */
let _noteTimer = null;
let _noteSaved = null;

/* -------------------------------------------------------------------------- */
/* Pure Helfer (Vitest)                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Pure — parst `#tisch/<token>`. Gibt `null` für alles andere zurück, damit der
 * bootstrap-Dispatcher unverändert weiterreichen kann.
 */
export function _parseTableHash(hash) {
  if (typeof hash !== 'string' || !hash) return null;
  const m = hash.match(/^#tisch\/(.+)$/);
  if (!m) return null;
  let token;
  try { token = decodeURIComponent(m[1]); } catch (_) { return null; }
  // S246-HF (P1) — alles ab `&`/`?` abschneiden. Der Hash IST die Route; hängt
  // jemand einen Parameter dahinter (Stripe tat das über die success_url), war
  // der Token vorher schlicht kaputt und der Gast landete auf der Startseite.
  // Der Edge baut die URL inzwischen richtig — das hier ist die zweite Linie,
  // denn ein kaputter Rückweg kostet den Gast seinen Beleg, nicht nur Komfort.
  token = String(token || '').split(/[&?]/)[0];
  token = token.trim();
  // Formatprüfung nur grob: der Server ist die Wahrheit. Zu strenge Regeln hier
  // würden bei einer künftigen Token-Länge die Seite blind machen.
  if (!token || token.length < 8 || token.length > 64) return null;
  return { token };
}

/**
 * Pure — wie lange bis zum nächsten Poll?
 *
 * Nicht sichtbar → gar nicht (ein Handy in der Hosentasche muss die Datenbank
 * nicht befragen). Nach Fehlern wächst der Abstand, damit ein kurzer
 * Funkloch-Aussetzer nicht in einen Sturm von Fehlversuchen kippt; gedeckelt,
 * damit die Seite nach einer langen Störung trotzdem von selbst zurückkommt.
 */
export function _nextPollDelay(visible, failStreak, baseMs) {
  if (!visible) return null;
  const base = Number.isFinite(baseMs) ? baseMs : POLL_MS;
  if (!failStreak) return base;
  return Math.min(base * Math.pow(2, Math.min(failStreak, 4)), 60000);
}

/**
 * Pure — Zustand eines Ruf-Knopfes aus `open_signals`.
 *
 * Der Knopf zeigt entweder „rufen" oder „ist gerufen · HH:MM". Bewusst KEIN
 * Countdown und kein Deaktivieren nach Zeit: solange der Ruf offen ist, ändert
 * ein weiterer Druck nichts (der Server antwortet `already_open`), und ein
 * abgelaufener Timer würde dem Gast suggerieren, der erste Ruf sei verloren.
 *
 * @returns {{called:boolean, at:string|null}}
 */
export function _signalState(openSignals, kind) {
  const list = Array.isArray(openSignals) ? openSignals : [];
  const hit = list.find(s => s && s.kind === kind);
  if (!hit) return { called: false, at: null };
  let at = null;
  if (hit.created_at) {
    const d = new Date(hit.created_at);
    if (!isNaN(d.getTime())) {
      at = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    }
  }
  return { called: true, at };
}

/**
 * Pure — was zeigt der Bezahl-Bereich? (S246 Cut D)
 *
 * Fünf Zustände, und die Reihenfolge ist Absicht:
 *   'off'     — der Betrieb bietet Online-Bezahlung am Tisch nicht an
 *   'nothing' — es gibt (noch) nichts zu zahlen
 *   'hold'    — dieser Gast ist gerade mitten in einer Zahlung
 *   'done'    — dieser Gast hat seins bezahlt, der Rest gehört anderen
 *   'pay'     — normal: hier ist der Knopf
 *
 * `done` steht bewusst VOR `pay`: wer schon gezahlt hat, soll nicht neben
 * seinem Beleg noch einen Bezahl-Knopf sehen, nur weil am Tisch noch etwas
 * offen ist. Wer trotzdem für andere mitzahlen will, tippt auf „auch für
 * andere zahlen" — das ist dann eine bewusste Entscheidung.
 *
 * S250 (B5) — `!o` heißt nicht mehr automatisch 'nothing'. Zahlt jemand die
 * GANZE Rechnung, ist die Bestellung bezahlt und fällt aus dem Session-Payload
 * (der Tisch soll für die nächste Runde frei sein). Genau der Gast bekam damit
 * nie seinen Beleg zu sehen — die Seite blieb auf „dein Beleg erscheint gleich
 * hier" stehen. `settled_receipts` (phase_184) trägt ihn jetzt nach.
 *
 * Für die ZUSTANDS-Entscheidung zählen die alten Belege bewusst nur, solange es
 * keine offene Bestellung gibt: bestellt der Gast eine zweite Runde, will er
 * „Online bezahlen" sehen und nicht „✅ Bezahlt" von vorhin. Sein Beleg von
 * eben bleibt trotzdem sichtbar — den rendert `_myReceipts`, nicht der Zustand.
 */
export function _payState(session) {
  const s = session || {};
  if (!s.stripe_dine_in_enabled) return 'off';
  const o = s.order;
  if (!o) return (s.settled_receipts || []).length ? 'done' : 'nothing';
  if (o.my_hold && o.my_hold.receipt_id) return 'hold';
  const open = Number(o.open_cents || 0);
  const minePaid = (o.paid_receipts || []).some(r => r && r.mine);
  if (open <= 0) return minePaid ? 'done' : 'nothing';
  if (minePaid) return 'done';
  return 'pay';
}

/**
 * Pure — die eigenen Belege dieses Gastes, aus beiden Quellen (S250/B5).
 *
 * `order.paid_receipts` gehört zur laufenden Bestellung und führt auch fremde
 * Zeilen (mit `mine: false`) — die filtert diese Funktion weg.
 * `settled_receipts` kommt aus abgeschlossenen Bestellungen derselben Belegung
 * und enthält von vornherein nur eigene (der Server bindet den Block an den
 * `guest_key`). Ein Beleg kann nie in beiden Listen stehen — sie schließen sich
 * über den Zahlungszustand der Bestellung aus —, die Entdopplung ist trotzdem
 * drin: doppelte Beleg-Knöpfe wären eine unschöne Art, das herauszufinden.
 */
export function _myReceipts(session) {
  const s = session || {};
  const current = ((s.order && s.order.paid_receipts) || []).filter(r => r && r.mine);
  const past    = (Array.isArray(s.settled_receipts) ? s.settled_receipts : []).filter(Boolean);
  const seen = new Set();
  const out  = [];
  for (const r of current.concat(past)) {
    const id = String(r.receipt_id || '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(r);
  }
  return out;
}

/** Pure — Überschrift der Runden-Zeile im Tisch-Status. */
export function _roundStatusLabel(rounds, roundNo) {
  const rows = (Array.isArray(rounds) ? rounds : []).filter(r => r && r.round_no === roundNo);
  if (!rows.length) return 'angenommen';
  const rank = { pending: 0, preparing: 1, ready: 2, served: 3 };
  const worst = rows.reduce((min, r) => Math.min(min, rank[r.status] ?? 0), 3);
  return ['angenommen', 'in der Küche', 'fertig', 'serviert'][worst] || 'angenommen';
}

/**
 * Pure — die RPC-Antwort in das Order-Objekt übersetzen, das der Bon-Renderer
 * erwartet (S254).
 *
 * `computeOrderReceipt()` liest die Positionen über `snapshotItems()` und
 * akzeptiert `tax7Cents` ebenso wie `tax7AmountCents`; die Umbenennung ist
 * deshalb flach. Wichtig sind `gross7Cents`/`gross19Cents`: nur mit ihnen nimmt
 * `grossPerRateFromOrder()` den GESPEICHERTEN Pfad (phase_174) und zeigt exakt
 * die Beträge, die auch signiert wurden. Rechnete der Bon sie selbst nach,
 * wären drei Cent Abweichung gegen die TSE-Signatur möglich — genau der Fehler
 * aus S241/HF-10.
 */
/**
 * Pure — Zeitpunkt in die zwei Formate zerlegen, die der Bon-Kopf erwartet
 * (S255/B1).
 *
 * `fmtDateDE()` hängt `T00:00:00` an, braucht also `YYYY-MM-DD`; `fmtTime()`
 * schneidet auf `HH:MM`. Ein ISO-String mit Zeitzone würde in beiden Fällen
 * Unsinn ergeben. Gerechnet wird in der Zeitzone des Betriebs — 21:30 in
 * Hamburg ist 19:30 UTC, und auf dem Beleg steht die Wanduhr.
 *
 * @returns {{date: string, time: string}} leere Strings, wenn nichts verwertbar
 */
export function _receiptDateParts(iso, timeZone) {
  const t = iso ? new Date(iso) : null;
  if (!t || isNaN(t.getTime())) return { date: '', time: '' };
  const tz = timeZone || 'Europe/Berlin';
  try {
    // `sv-SE` liefert YYYY-MM-DD — genau das Format, das fmtDateDE erwartet.
    const date = new Intl.DateTimeFormat('sv-SE', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(t);
    const time = new Intl.DateTimeFormat('de-DE', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false
    }).format(t);
    return { date, time };
  } catch (_) {
    // Exotische Laufzeit ohne die Zeitzonen-Daten — lieber die Zeile ohne
    // Uhrzeit als eine falsche Uhrzeit.
    return { date: t.toISOString().slice(0, 10), time: '' };
  }
}

export function _receiptOrderFromView(view) {
  const r = view || {};
  const items = (Array.isArray(r.items) ? r.items : []).map(i => ({
    name:             (i && i.name) || '',
    qty:              Number(i && i.qty) || 1,
    unit_price_cents: Number(i && i.unit_price_cents) || 0,
    line_total_cents: Number(i && i.line_total_cents) || 0,
    tax_rate:         Number(i && i.tax_rate) || 7
  }));
  const tse = r.tse || {};
  // S255 (B1) — auf dem Gast-Beleg ist mit „Datum" der AUSSTELLUNGS-Zeitpunkt
  // gemeint, nicht der Abholtermin: der Beleg entsteht mit der Zahlung, und der
  // Gast sitzt am Tisch. `guest_receipt_view` liefert `pickup_date`/`-time`
  // deshalb bewusst nicht nach; die beiden Felder tragen hier den Zahlzeitpunkt.
  // (Bei einer Tisch-Bestellung ist das ohnehin derselbe Tag.)
  const stamp = _receiptDateParts(r.paid_at);
  return {
    id:                  r.receipt_id || '',
    invoiceNumber:       r.invoice_number || '',
    orderNumber:         r.order_number || '',
    paidAt:              r.paid_at || null,
    pickupDate:          stamp.date,
    pickupTime:          stamp.time,
    paymentMethod:       r.payment_method || '',
    paymentStatus:       'paid',
    totalGrossCents:     Number(r.total_gross_cents) || 0,
    subtotalNetCents:    Number(r.subtotal_net_cents) || 0,
    tax7Cents:           Number(r.tax_7_amount_cents) || 0,
    tax19Cents:          Number(r.tax_19_amount_cents) || 0,
    // Beide Schreibweisen: `grossPerRateFromOrder` prüft auf die snake_case-
    // Felder, der Rest des Renderers auf camelCase.
    gross7Cents:         Number(r.gross_7_cents) || 0,
    gross19Cents:        Number(r.gross_19_cents) || 0,
    gross_7_cents:       Number(r.gross_7_cents) || 0,
    gross_19_cents:      Number(r.gross_19_cents) || 0,
    preorderSnapshot:    { version: 2, items },
    tseTransactionId:    tse.transaction_id || null,
    tseSignatureCounter: tse.signature_counter || null,
    tseSignatureValue:   tse.signature_value || null,
    tseSignedAt:         tse.signed_at || null,
    tseQrPayload:        tse.qr_payload || null,
    tseMode:             tse.mode || null
  };
}

/**
 * Pure — Betriebsangaben für den Belegkopf aus der RPC-Antwort (S254).
 *
 * Die Gast-Seite hat keinen `window.App.storage`-State; die Angaben kommen aus
 * `guest_receipt_view`. Fehlt ein Feld (älterer DB-Stand vor phase_186), bleibt
 * es leer — der Kopf zeigt dann, was da ist, statt gar nichts.
 */
export function _receiptCompanyFromView(view) {
  const b = (view && view.restaurant) || {};
  return {
    name:      b.legal_name || b.name || '',
    brand:     b.brand || b.name || '',
    street:    b.street || '',
    zip:       b.zip || '',
    city:      b.city || '',
    phone:     b.phone || '',
    email:     b.email || '',
    ustId:     b.ust_id || '',
    // `tax_id` ist der Bestandsschlüssel: liefert ein alter Server nur ihn,
    // landet er als Steuernummer im Kopf statt zu verschwinden.
    taxNumber: b.tax_number || (b.ust_id ? '' : (b.tax_id || ''))
  };
}

/** Pure — Logo-Quelle mit Cache-Bust, wie `getBrandLogoSrc()` im Cockpit. */
export function _receiptLogoFromView(view) {
  const b = (view && view.restaurant) || {};
  const url = String(b.logo_url || '').trim();
  if (!url) return '';
  const stamp = b.logo_uploaded_at ? String(b.logo_uploaded_at) : '';
  if (!stamp) return url;
  return url + (url.includes('?') ? '&' : '?') + 'v=' + encodeURIComponent(stamp);
}

/* -------------------------------------------------------------------------- */
/* Identität                                                                   */
/* -------------------------------------------------------------------------- */

function _readStore(key) {
  try { return window.localStorage.getItem(key); } catch (_) { return null; }
}
function _writeStore(key, val) {
  try { window.localStorage.setItem(key, val); } catch (_) { /* Privat-Modus */ }
}

/** guest_key je Tisch-Token: ein Handy bleibt „derselbe Gast", solange es an
 *  diesem Tisch sitzt. Kein Personenbezug — eine Zufalls-UUID. */
/* --------------------------- Ansichts-Zustand (S251) ---------------------- */

function _loadUi() {
  let raw = null;
  try { raw = JSON.parse(_readStore(UI_PREFIX + _token) || 'null'); } catch (_) { raw = null; }
  const o = raw && typeof raw === 'object' ? raw : {};
  _ui = {
    menu:   (o.menu === true || o.menu === false) ? o.menu : null,
    groups: Array.isArray(o.groups) ? o.groups.map(String) : [],
    lines:  Array.isArray(o.lines)  ? o.lines.map(String)  : []
  };
}

function _saveUi() {
  // Die Listen sind gedeckelt: ein Warenkorb hat selten mehr als ein paar
  // Dutzend Zeilen, und eine unbegrenzt wachsende Liste alter Ids im
  // localStorage wäre Müll, den niemand mehr aufräumt.
  _writeStore(UI_PREFIX + _token, JSON.stringify({
    menu:   _ui.menu,
    groups: _ui.groups.slice(-20),
    lines:  _ui.lines.slice(-60)
  }));
}

/** Ist die Speisekarte eingeklappt? `null` im Zustand = Automatik. */
function _menuCollapsed() {
  if (_ui.menu === true || _ui.menu === false) return _ui.menu;
  return !!(_session && _session.order);
}

/** Zugeklappte Gäste-Gruppen. Default: eigene offen, fremde zu (S251/U4). */
function _collapsedGroups(cart) {
  const set = new Set();
  for (const r of (Array.isArray(cart) ? cart : [])) {
    if (!r) continue;
    const key = String(r.guest_key || '?');
    if (set.has(key)) continue;
    const remembered = _ui.groups.includes('+' + key) ? false
                     : _ui.groups.includes('-' + key) ? true
                     : null;
    if (remembered === null) { if (!r.mine) set.add(key); }
    else if (remembered) set.add(key);
  }
  return set;
}

function ensureGuestKey(token) {
  const k = GUEST_KEY_PREFIX + token;
  let v = _readStore(k);
  if (!v) {
    v = (window.crypto && typeof window.crypto.randomUUID === 'function')
      ? window.crypto.randomUUID()
      : 'g-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
    _writeStore(k, v);
  }
  return v;
}

/* -------------------------------------------------------------------------- */
/* Server-Zugriff                                                              */
/* -------------------------------------------------------------------------- */

function _supa() {
  return (window.App && window.App.supabase) || null;
}

async function _rpc(fn, args) {
  const supa = _supa();
  if (!supa) throw new Error('Verbindung nicht bereit.');
  const { data, error } = await supa.rpc(fn, args);
  if (error) throw error;
  return data;
}

/* -------------------------------------------------------------------------- */
/* Poll                                                                        */
/* -------------------------------------------------------------------------- */

function _visible() {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function _scheduleNextPoll() {
  clearTimeout(_pollTimer);
  const delay = _nextPollDelay(_visible(), _failStreak, POLL_MS);
  if (delay === null) return;                 // unsichtbar → Pause
  _pollTimer = setTimeout(() => { refreshSession().catch(() => {}); }, delay);
}

/** Session neu laden und die Sichten patchen. `immediate` überspringt den Timer. */
export async function refreshSession() {
  if (!_token) return;
  const myToken = ++_pollToken;
  try {
    const data = await _rpc('get_table_session', { p_token: _token, p_guest_key: _guestKey });
    if (myToken !== _pollToken) return;       // eine neuere Antwort war schneller
    _failStreak = 0;
    _session = data || null;
    renderTableView();
  } catch (err) {
    if (myToken !== _pollToken) return;
    _failStreak += 1;
    console.warn('[customer-table] Poll:', err && err.message);
  } finally {
    if (myToken === _pollToken) _scheduleNextPoll();
  }
}

function _bindVisibility() {
  if (_visibilityBound || typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (!_token) return;
    if (_visible()) { refreshSession().catch(() => {}); }
    else clearTimeout(_pollTimer);
  });
  _visibilityBound = true;
}

/* -------------------------------------------------------------------------- */
/* Einstieg                                                                    */
/* -------------------------------------------------------------------------- */

/** bootstrap-Dispatcher. `true` = Hash gehörte uns. */
export function checkTableRequestFromUrl() {
  const route = _parseTableHash(window.location.hash);
  if (!route) return false;
  _token = route.token;
  _guestKey = ensureGuestKey(_token);
  _guestName = _readStore(GUEST_NAME_PREFIX + _token) || '';
  _loadUi();
  // Neuer Scan / Reload: der Merker „das ging schon raus" gehört zur alten
  // Sitzung. Ohne den Reset schwiege ein Speichern, das nach dem Neuladen
  // denselben Text noch einmal senden müsste.
  clearTimeout(_noteTimer);
  _noteTimer = null;
  _noteSaved = null;
  _menuMounted = false;
  _sheetOpen = false;
  _sheetMode = 'cart';
  _failStreak = 0;
  if (typeof window.switchView === 'function') window.switchView('table');
  _bindVisibility();
  _renderShell();
  _handleTableStripeReturn();
  refreshSession().catch(() => {});
  return true;
}

/**
 * Rückkehr von Stripe auf der Tisch-Route (S246).
 *
 * Eigener Handler statt `handleStripeReturnFromUrl`: der springt auf die
 * Tracking-Seite der zuletzt gemerkten Order dieses Geräts — hier wurde aber
 * ein Teilbeleg bezahlt, und der Gast will an seinem Tisch bleiben.
 *
 * Der Toast ist bewusst vorsichtig formuliert: bezahlt ist die Zahlung erst,
 * wenn der Webhook durch ist. Der Poll holt den Beleg Sekunden später nach —
 * bis dahin verspricht die Seite nichts, was sie nicht weiß.
 */
function _handleTableStripeReturn() {
  let action = null;
  try {
    const q = window.location.search || '';
    if (/[?&]stripe_return=1\b/.test(q)) action = 'return';
    else if (/[?&]stripe_cancel=1\b/.test(q)) action = 'cancel';
  } catch (_) { return; }
  if (!action) return;

  if (action === 'return') {
    showToast('Danke! Die Zahlung wird bestätigt — dein Beleg erscheint gleich hier.', 'success');
  } else {
    showToast('Zahlung abgebrochen. Du kannst es erneut versuchen oder beim Service zahlen.', 'info');
  }
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('stripe_return');
    url.searchParams.delete('stripe_cancel');
    url.searchParams.delete('session_id');
    window.history.replaceState(null, '', url.pathname + (url.search || '') + (url.hash || ''));
  } catch (_) { /* alte Browser — nicht fatal */ }
}

function _root() { return document.getElementById('table-view-body'); }

/* -------------------------------------------------------------------------- */
/* Non-destruktiv ersetzen                                                     */
/* -------------------------------------------------------------------------- */

/** Zuletzt GESCHRIEBENES Markup je Container-Id. */
const _lastHtml = new Map();

/**
 * Einen Block nur dann neu schreiben, wenn sich sein Markup wirklich geändert
 * hat (S252/N2).
 *
 * Das nahe liegende `if (el.innerHTML !== next)` funktioniert hier NICHT — und
 * zwar strukturell, nicht zufällig: `dom-bindings.js` wandelt jeden
 * `onclick="…"`-Handler in einen echten Listener um und **entfernt danach das
 * Attribut** (`removeAttribute`, CSP-Regel aus S23). Was im DOM steht, ist
 * damit nie wieder das, was wir hineingeschrieben haben — der Vergleich schlägt
 * bei JEDEM Poll fehl, der Block wird neu gebaut, `dom-bindings` konvertiert
 * erneut, und das Spiel beginnt von vorn. Live gemessen (S251-Retest, Befund
 * B1): 7 Mutationen in 28 s Leerlauf, je 10 Knoten raus und 10 rein, bei
 * identischem `innerHTML` vorher/nachher.
 *
 * Verglichen wird deshalb gegen das, was wir zuletzt geschrieben haben, nicht
 * gegen das, was der Browser daraus gemacht hat. Ein Block ohne Inline-Handler
 * (die Ruf-Knöpfe vor S251) war davon nie betroffen — deshalb ist das Muster
 * jahrelang nicht aufgefallen.
 *
 * @returns {boolean} `true`, wenn wirklich geschrieben wurde.
 */
function _patchHtml(el, html) {
  if (!el) return false;
  const key = el.id || '';
  // Sicherheitsnetz: hat jemand anders den Container geleert (Shell-Neuaufbau,
  // Sperr-Seite), wird trotz gleichem Cache-Eintrag geschrieben.
  const emptied = html !== '' && el.childNodes.length === 0;
  if (!emptied && _lastHtml.get(key) === html) return false;
  el.innerHTML = html;
  _lastHtml.set(key, html);
  return true;
}

/** Grundgerüst EINMAL zeichnen; der Poll patcht danach nur noch Teile. Ein
 *  vollständiges Neuzeichnen würde bei jedem Tick den Scroll der Speisekarte
 *  und ein offenes Konfigurations-Modal wegräumen. */
function _renderShell() {
  const root = _root();
  if (!root) return;
  // Neue Container, neuer Vergleichsstand — sonst hielte `_patchHtml` einen
  // Block fuer unveraendert, den es gar nicht mehr gibt.
  _lastHtml.clear();
  root.innerHTML = `
    <div class="ct-head">
      <div class="ct-head-line" id="ct-head-line">Tisch wird geladen …</div>
      <div class="ct-name-row" id="ct-name-row"></div>
    </div>
    <div class="ct-actions" id="ct-actions"></div>
    <div id="ct-status"></div>
    <div id="ct-pay"></div>
    <div class="ct-menu-folded" id="ct-menu-folded" hidden></div>
    <div id="table-menu-root"></div>
    <div class="ct-bar" id="ct-bar" hidden>
      <button type="button" class="ct-bar-btn" onclick="openTableCartSheet()">
        <span id="ct-bar-text">Noch nichts im Warenkorb</span>
        <span class="ct-bar-cta">Zum Warenkorb →</span>
      </button>
    </div>
    <div class="ct-sheet-backdrop" id="ct-sheet-backdrop" hidden onclick="closeTableCartSheet()"></div>
    <aside class="ct-sheet" id="ct-sheet" hidden onclick="event.stopPropagation()"></aside>`;
}

/* -------------------------------------------------------------------------- */
/* Render                                                                      */
/* -------------------------------------------------------------------------- */

export function renderTableView() {
  const root = _root();
  if (!root || !_session) return;

  const head = document.getElementById('ct-head-line');
  if (head) {
    head.textContent = _session.table_name
      ? `Tisch ${_session.table_name} · ${_session.restaurant || ''}`.trim()
      : (_session.restaurant || 'Grill & Green');
  }

  if (_session.state !== 'open') { _renderLocked(); return; }
  _renderNameRow();
  _renderActions();
  _renderStatus();
  _renderPay();
  _mountMenuOnce();
  _renderMenuFold();
  _renderBar();
  if (_sheetOpen) _renderSheet();
}

const LOCK_TEXT = {
  manual:            'Der Service hat die Handy-Bestellung an diesem Tisch gerade pausiert.',
  overdue:           'Die Tischzeit ist abgelaufen.',
  reservation:       'Für diesen Tisch steht gleich eine Reservierung an.',
  ordering_disabled: 'Bestellen am Tisch ist gerade nicht verfügbar – bitte wende dich an unser Team.',
  no_occupancy:      'Dieser Tisch ist noch nicht freigegeben.'
};

function _renderLocked() {
  const nameRow = document.getElementById('ct-name-row');
  if (nameRow) nameRow.innerHTML = '';
  _patchHtml(document.getElementById('ct-actions'), '');
  _patchHtml(document.getElementById('ct-pay'), '');
  const folded = document.getElementById('ct-menu-folded');
  if (folded) { folded.hidden = true; _patchHtml(folded, ''); }
  const menu = document.getElementById('table-menu-root');
  if (menu) { menu.hidden = false; menu.innerHTML = ''; }
  const bar = document.getElementById('ct-bar');
  if (bar) bar.hidden = true;
  const status = document.getElementById('ct-status');
  if (!status) return;
  const reason = LOCK_TEXT[_session.reason] || LOCK_TEXT.no_occupancy;
  const cta = lockCtaText(_session.reason);
  status.innerHTML = `
    <div class="ct-locked">
      <div class="ct-locked-icon">🔒</div>
      <h2>${_session.table_name ? `Tisch ${htmlEscape(String(_session.table_name))}` : 'Dieser Tisch'}</h2>
      <p>${htmlEscape(reason)}</p>
      ${cta ? `<p class="ct-locked-cta">${htmlEscape(cta)}</p>` : ''}
    </div>`;
  _menuMounted = false;
}

/**
 * Pure — Handlungsaufforderung unter der Sperr-Meldung (S267 K4).
 *
 * Bei `ordering_disabled` ist der Kanal „Bestellen am Tisch" GLOBAL aus: das
 * Team kann keinen einzelnen Tisch freischalten, also keine CTA, die genau das
 * verspricht. Bei allen anderen Gründen (manuell zu, überzogen, Reservierung,
 * keine Belegung) bleibt sie — dort hilft der Service wirklich weiter.
 */
export function lockCtaText(reason) {
  if (reason === 'ordering_disabled') return '';
  return 'Bitte kurz beim Service melden — die Kollegen schalten den Tisch frei.';
}

function _renderNameRow() {
  const row = document.getElementById('ct-name-row');
  if (!row) return;
  // Nicht neu zeichnen, während getippt wird — sonst springt der Cursor bei
  // jedem Poll-Tick weg.
  if (row.querySelector('#ct-name-input') === document.activeElement) return;
  row.innerHTML = _guestName
    ? `<span class="ct-name-chip">Du bestellst als <strong>${htmlEscape(_guestName)}</strong></span>
       <button type="button" class="ct-name-edit" onclick="editTableGuestName()">ändern</button>`
    : `<label class="ct-name-label" for="ct-name-input">Wie heißt du? <span>(freiwillig)</span></label>
       <input id="ct-name-input" class="ct-name-input" type="text" maxlength="30" placeholder="Vorname"
              onchange="setTableGuestName(this.value)">`;
}

/**
 * Die Aktionsleiste (S251/U2) — alles, was der Gast tun kann, an einer Stelle.
 *
 * Vorher lagen die Aktionen an drei Orten: die Ruf-Knöpfe über der Karte, das
 * Bezahlen darunter, der Warenkorb ganz unten. Wer am Handy durch die Karte
 * scrollt, sah keine davon. Die Leiste klebt jetzt oben (`position: sticky`) und
 * ist damit immer erreichbar.
 *
 * Vier Knöpfe, und ausdrücklich KEIN „💳 Bezahlen" (S252/N1): der doppelte sich
 * mit „💶 Zahlen bitte", und der grosse Block im Fluss (`#ct-pay`) ist der
 * präsentere und bessere Weg zum Online-Bezahlen. „Zahlen bitte" bleibt hier,
 * weil es der Service-RUF ist und nicht der Bezahl-Weg.
 *
 * Zustände unverändert: „💶 Zahlen bitte" erst mit Bestellung — ein Gast, der
 * noch nichts bestellt hat, soll nicht nach der Rechnung fragen können; ein
 * offener Ruf zeigt sich als gedrückter Knopf mit Uhrzeit.
 *
 * Non-destruktiv über `_patchHtml` (Memory `feedback_gg_non_destructive_render`)
 * — der naive `innerHTML`-Vergleich taugt hier nicht, Begründung dort.
 */
function _renderActions() {
  const box = document.getElementById('ct-actions');
  if (!box) return;
  const signals = (_session && _session.open_signals) || [];
  const hasOrder = !!(_session && _session.order);
  const svc = _signalState(signals, 'call_service');
  const pay = _signalState(signals, 'call_pay');
  const cart = (_session && _session.cart) || [];
  const lines = cartSummary(cart).lines;
  const folded = _menuCollapsed();

  const act = (cls, handler, icon, label, extra = '') => `
    <button type="button" class="ct-act${cls}" onclick="${handler}">
      <span class="ct-act-icon">${icon}</span>
      <span class="ct-act-text">${htmlEscape(label)}${extra}</span>
    </button>`;

  // Die Uhrzeit steht nur dran, wenn es eine gibt — „Service gerufen · " mit
  // leerem Rest wäre schlechter als gar keine Zeit.
  const called = state => (state.called && state.at) ? ` · ${htmlEscape(state.at)}` : '';

  const next =
      act('', 'toggleTableMenu()', folded ? '➕' : '🍽️', folded ? 'Nachbestellen' : 'Karte')
    + act(lines ? ' has-items' : '', 'openTableCartSheet()', '🛒', 'Warenkorb',
          lines ? ` <span class="ct-act-count">${lines}</span>` : '')
    + act(svc.called ? ' is-called' : '', "callTableService('call_service')", '🔔',
          svc.called ? 'Service gerufen' : 'Service', called(svc))
    + (hasOrder ? act(pay.called ? ' is-called' : '', "callTableService('call_pay')", '💶',
          pay.called ? 'Rechnung angefragt' : 'Zahlen bitte', called(pay)) : '');

  _patchHtml(box, next);
}

/**
 * Speisekarte auf- oder zuklappen (S251/U3).
 *
 * Vor der ersten abgeschickten Runde ist die Karte offen — da will der Gast
 * genau dorthin. Sobald eine Runde draussen ist, klappt sie zu: sonst scrollt
 * er bei jedem Blick auf seinen Bestellstatus an der ganzen Karte vorbei.
 *
 * Der Picker wird dabei NUR VERBORGEN, nicht abgeräumt. `initMenuPicker` ein
 * zweites Mal laufen zu lassen ist der direkte Weg zurück in das
 * Runde-2-Item-Cache-Ticket (S244 T1) — und ein zerstörter und neu gebauter
 * Picker verlöre ausserdem Scroll-Position und Filter.
 */
function _renderMenuFold() {
  const menu = document.getElementById('table-menu-root');
  const folded = document.getElementById('ct-menu-folded');
  if (!menu || !folded) return;
  const collapse = _menuCollapsed();
  if (menu.hidden !== collapse) menu.hidden = collapse;
  folded.hidden = !collapse;
  if (!collapse) { _patchHtml(folded, ''); return; }
  const next = `
    <button type="button" class="ct-menu-open" onclick="toggleTableMenu()">
      <span class="ct-menu-open-icon">➕</span>
      <span>Nachbestellen — Speisekarte öffnen</span>
    </button>`;
  _patchHtml(folded, next);
}

/**
 * Die Bestellung des Tisches.
 *
 * S248 — Positionen, die an einem BEZAHLTEN Beleg hängen, tragen ein ✅. Ohne
 * das sah ein Mitgast die 11,90 € seines Nachbarn weiter als offen und fragte
 * sich, warum die Summe nicht sinkt. Bewusst nur ein Häkchen: weder Nummer noch
 * Betrag eines fremden Belegs gehören ihn etwas an. Das Feld kommt schon aus
 * `get_table_session` (`settled`, seit phase_179) — keine neue anon-Spalte,
 * keine neue Abfrage (T4-Regel).
 */
function _renderStatus() {
  const box = document.getElementById('ct-status');
  if (!box) return;
  const o = _session.order;
  if (!o) { box.innerHTML = ''; return; }
  const rounds = Array.from(new Set((o.items || []).map(i => i.round_no))).sort((a, b) => a - b);
  box.innerHTML = `
    <section class="ct-order">
      <h2>Deine Bestellung · ${htmlEscape(o.order_number || '')}</h2>
      ${rounds.map(rn => `
        <div class="ct-round">
          <div class="ct-round-head">
            <span>${rounds.length > 1 ? `Runde ${rn}` : 'Bestellung'}</span>
            <span class="ct-round-status">${htmlEscape(_roundStatusLabel(o.rounds, rn))}</span>
          </div>
          ${(o.items || []).filter(i => i.round_no === rn).map(i => `
            <div class="ct-round-line${i.settled ? ' is-settled' : ''}">
              <span>${i.settled ? '<span class="ct-settled" title="Bereits bezahlt">✅</span> ' : ''}${i.qty}× ${htmlEscape(i.name || '')}${i.guest_name ? ` <em>(${htmlEscape(i.guest_name)})</em>` : ''}</span>
              <span>${htmlEscape(fmtEUR((Number(i.line_total_cents) || 0) / 100))}</span>
            </div>`).join('')}
        </div>`).join('')}
      <div class="ct-order-total">
        <span>Summe</span><strong>${htmlEscape(fmtEUR((Number(o.total_gross_cents) || 0) / 100))}</strong>
      </div>
      <p class="ct-order-hint">Bezahlt wird beim Service. Nachbestellen geht jederzeit — einfach weiter in der Karte tippen.</p>
    </section>`;
}

/**
 * Der Bezahl-Bereich (S246 Cut D).
 *
 * Nicht-destruktiv wie alles hier: wird bei jedem Poll-Tick gerufen, ersetzt
 * aber nur, wenn sich das HTML tatsächlich ändert — sonst blitzt der Knopf im
 * Vier-Sekunden-Takt.
 */
function _renderPay() {
  const box = document.getElementById('ct-pay');
  if (!box) return;
  const o = (_session && _session.order) || null;
  const state = _payState(_session);
  // S250 (B5): auch die Belege aus bereits abgeschlossenen Bestellungen dieser
  // Belegung — sonst verschwindet der Beleg genau bei dem Gast, der alles zahlt.
  const mine = _myReceipts(_session);

  const belege = mine.length
    ? `<div class="ct-pay-receipts">${mine.map(r => `
        <button type="button" class="ct-pay-receipt"
                onclick="openGuestReceipt('${htmlEscape(String(r.receipt_id))}')">
          📄 Beleg ${htmlEscape(String(r.invoice_number || ''))}
          · ${htmlEscape(fmtEUR((Number(r.total_gross_cents) || 0) / 100))}
        </button>`).join('')}</div>`
    : '';

  let next = '';
  if (state === 'pay') {
    next = `
      <section class="ct-pay">
        <button type="button" class="ct-pay-btn" onclick="openTablePaySheet()">
          💳 Online bezahlen
        </button>
        <p class="ct-pay-hint">Oder ganz normal beim Service zahlen.</p>
      </section>`;
  } else if (state === 'hold') {
    next = `
      <section class="ct-pay">
        <button type="button" class="ct-pay-btn is-pending"
                onclick="resumeTablePay()">
          💳 Zahlung fortsetzen ·
          ${htmlEscape(fmtEUR((Number(o.my_hold.total_gross_cents) || 0) / 100))}
        </button>
        <p class="ct-pay-hint">Deine Positionen sind so lange für dich reserviert.</p>
      </section>`;
  } else if (state === 'done') {
    const open = Number((o && o.open_cents) || 0);
    next = `
      <section class="ct-pay">
        <div class="ct-pay-done">✅ Bezahlt — danke!</div>
        ${belege}
        ${open > 0 ? `
          <button type="button" class="ct-pay-more" onclick="openTablePaySheet()">
            Auch für andere zahlen (${htmlEscape(fmtEUR(open / 100))} offen)
          </button>` : ''}
      </section>`;
  }
  if (state === 'done') {
    // Belege stehen im Block oben schon drin.
  } else if (belege) {
    next += `<section class="ct-pay">${belege}</section>`;
  }
  _patchHtml(box, next);
}

function _mountMenuOnce() {
  if (_menuMounted) return;
  const host = document.getElementById('table-menu-root');
  if (!host) return;

  // Die Speisekarte im Cache gehört dem Mandanten aus `config.js` — das ist der
  // Mandant DIESER Auslieferung. Löst das Token auf einen anderen auf, wäre die
  // gezeigte Karte schlicht falsch; dann lieber gar keine als eine fremde.
  // (Ein Menü je Mandant nachzuladen ist ein eigener Schritt, sobald mehrere
  //  Tenants auf derselben Domain laufen — heute tut das keiner.)
  const cfgRid = (window.AppConfig && window.AppConfig.defaultRestaurantId) || null;
  if (_session.restaurant_id && cfgRid && _session.restaurant_id !== cfgRid) {
    console.error('[customer-table] Token gehoert zu Mandant', _session.restaurant_id,
                  '— diese Auslieferung bedient', cfgRid);
    host.innerHTML = `<p class="ct-empty">Diese Karte lässt sich hier gerade nicht laden.
      Bitte kurz beim Service melden.</p>`;
    _menuMounted = true;
    return;
  }

  _menuMounted = true;
  initMenuPicker('table-menu-root', { onLineConfirmed: addLineToTableCart });
}

function _renderBar() {
  const bar = document.getElementById('ct-bar');
  const txt = document.getElementById('ct-bar-text');
  if (!bar || !txt) return;
  const cart = (_session && _session.cart) || [];
  bar.hidden = false;
  // Nur den Text patchen — die Leiste selbst bleibt stehen, sonst blitzt sie
  // bei jedem Poll-Tick.
  const next = cartBarText(cart);
  if (txt.textContent !== next) txt.textContent = next;
  bar.classList.toggle('is-empty', cartSummary(cart).lines === 0);
}

function _renderSheet() {
  const sheet = document.getElementById('ct-sheet');
  const back = document.getElementById('ct-sheet-backdrop');
  if (!sheet || !back) return;
  sheet.hidden = false;
  back.hidden = false;
  if (_sheetMode === 'pay')     { sheet.innerHTML = _paySheetHtml();     return; }
  if (_sheetMode === 'receipt') { _renderReceiptSheet(sheet); return; }
  // S253 — solange der Gast in seinem Hinweis tippt, wird das Sheet NICHT
  // ersetzt. Sonst verlöre er mitten im Satz Fokus und Cursor-Position (dasselbe
  // Muster wie `_renderNameRow`). Der nächste Tick nach dem Verlassen holt alles
  // nach, was inzwischen am Tisch passiert ist.
  const noteEl = document.getElementById('ct-note-input');
  if (noteEl && noteEl === document.activeElement) return;

  const cart = (_session && _session.cart) || [];
  const next = renderCartSheet(cart, {
    tableName:       _session && _session.table_name,
    roundNo:         nextRoundNo(_session && _session.order),
    collapsedGroups: _collapsedGroups(cart),
    openLines:       new Set(_ui.lines),
    myNote:          myGuestNote(_session),
    foreignNotes:    foreignGuestNotes(_session)
  });
  // S251 — auch hier nur bei echter Änderung ersetzen. Das Sheet bleibt beim
  // Durchsehen offen, und der Poll läuft weiter: würde es alle vier Sekunden
  // neu gebaut, klappte jede geöffnete Detailzeile zu und die Liste sprünge.
  _patchHtml(sheet, next);
}

/**
 * Den Beleg zeichnen — und zwar HÖCHSTENS EINMAL je Beleg (S254).
 *
 * Das Sheet wird wie alles hier im Vier-Sekunden-Takt neu gezeichnet. Würde das
 * iframe dabei jedes Mal neu gebaut, flackerte der Bon, verlöre die
 * Scroll-Position und lüde das Logo neu — dieselbe Regel wie `_patchHtml()`
 * seit S252, nur an einem Element, das man nicht vergleichen kann.
 */
function _renderReceiptSheet(sheet) {
  const id = (_receiptView && _receiptView.receipt_id) ? String(_receiptView.receipt_id) : '';
  if (sheet.getAttribute('data-receipt') === id && sheet.querySelector('#ct-receipt-frame')) {
    return;                                   // steht schon, unverändert
  }
  sheet.innerHTML = _receiptSheetHtml();
  sheet.setAttribute('data-receipt', id);
  const frame = sheet.querySelector('#ct-receipt-frame');
  if (frame && _receiptHtml && _receiptHtmlFor === id) frame.srcdoc = _receiptHtml;
}

/**
 * „Nur meine Positionen" / „Ganze Rechnung" — mit den Beträgen, die der Server
 * IM MOMENT DER FRAGE nennt. Aus dem Poll-Stand gerechnet wären sie bis zu vier
 * Sekunden alt, und in genau diesen vier Sekunden kann jemand anders bezahlt
 * oder nachbestellt haben.
 */
function _paySheetHtml() {
  const o = (_session && _session.order) || {};
  const opt = _payOptions || { mine_cents: 0, all_cents: 0 };
  const mine = Number(opt.mine_cents) || 0;
  const all  = Number(opt.all_cents) || 0;
  const row = (scope, label, cents, hint) => `
    <button type="button" class="ct-pay-option" onclick="startTablePay('${scope}')"
            ${cents > 0 ? '' : 'disabled'}>
      <span class="ct-pay-option-label">${htmlEscape(label)}</span>
      <span class="ct-pay-option-sum">${htmlEscape(fmtEUR(cents / 100))}</span>
      <span class="ct-pay-option-hint">${htmlEscape(hint)}</span>
    </button>`;
  return `
    <div class="ct-sheet-head">
      <h2>Online bezahlen</h2>
      <button type="button" class="ct-sheet-close" onclick="closeTableCartSheet()">×</button>
    </div>
    <div class="ct-pay-options">
      ${row('mine', 'Nur meine Positionen', mine,
            mine > 0 ? 'Was du selbst bestellt hast.' : 'Du hast noch nichts Offenes.')}
      ${row('all', 'Ganze Rechnung', all,
            'Alles, was am Tisch noch offen ist.')}
    </div>
    <p class="ct-sheet-hint">
      Du wirst zu Stripe weitergeleitet und kommst danach hierher zurück.
      ${o.order_number ? `Bestellung ${htmlEscape(String(o.order_number))}.` : ''}
    </p>`;
}

/**
 * Der Beleg — DERSELBE Bon wie im Archiv, als PDF, im Druck und in der Mail
 * (S254, Jonas-Entscheid „Denselben Bon wie überall sonst").
 *
 * Das Dokument kommt aus `buildStandaloneReceiptHtml()` und bringt sein eigenes
 * `<style>` mit. Es landet deshalb in einem **iframe** und nie per `innerHTML`
 * in der Seite: sonst gälten die Bon-Regeln global und färbten die Gast-Seite
 * ein (das war der S230-Fehler).
 *
 * `sandbox` erlaubt genau zwei Dinge: `allow-same-origin`, damit `print()` von
 * aussen an das Dokument kommt, und `allow-modals` für den Druck-Dialog. Keine
 * Skripte — der Bon braucht keine.
 */
function _receiptSheetHtml() {
  if (!_receiptView) {
    return `
      <div class="ct-sheet-head">
        <h2>Beleg wird geladen …</h2>
        <button type="button" class="ct-sheet-close" onclick="closeTableCartSheet()">×</button>
      </div>`;
  }
  const nr = _receiptView.invoice_number || '';
  return `
    <div class="ct-sheet-head">
      <h2>Beleg${nr ? ' ' + htmlEscape(String(nr)) : ''}</h2>
      <button type="button" class="ct-sheet-close" onclick="closeTableCartSheet()">×</button>
    </div>
    <iframe id="ct-receipt-frame" class="ct-receipt-frame" title="Beleg"
            sandbox="allow-same-origin allow-modals"></iframe>
    <button type="button" class="ct-pay-more" onclick="printGuestReceipt()">Beleg drucken / speichern</button>`;
}

/* -------------------------------------------------------------------------- */
/* Aktionen (Inline-Handler → window)                                          */
/* -------------------------------------------------------------------------- */

export function setTableGuestName(value) {
  const v = String(value || '').trim().slice(0, 30);
  _guestName = v;
  _writeStore(GUEST_NAME_PREFIX + _token, v);
  _renderNameRow();
  // Der Name wandert beim nächsten Einlegen mit; bereits eingelegte Zeilen zieht
  // `table_cart_add` serverseitig nach.
  if (v) refreshSession().catch(() => {});
}

export function editTableGuestName() {
  _guestName = '';
  _renderNameRow();
  const el = document.getElementById('ct-name-input');
  if (el) el.focus();
}

export function openTableCartSheet() {
  _sheetOpen = true;
  _sheetMode = 'cart';
  _renderSheet();
}

/* --------------------------- Auf- und Zuklappen (S251) -------------------- */

/** Speisekarte auf/zu — die erste Handlung schaltet die Automatik ab. */
export function toggleTableMenu() {
  _ui.menu = !_menuCollapsed();
  _saveUi();
  _renderActions();
  _renderMenuFold();
  // Beim Öffnen dorthin springen, wo die Karte anfängt — sonst steht der Gast
  // nach dem Tipp weiter am unteren Ende seines Bestellstatus.
  if (!_ui.menu) {
    const menu = document.getElementById('table-menu-root');
    if (menu && typeof menu.scrollIntoView === 'function') {
      menu.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}

/** Eine Gäste-Gruppe im Warenkorb auf/zu. */
export function toggleCartGroup(guestKey) {
  const key = String(guestKey || '');
  if (!key) return;
  const cart = (_session && _session.cart) || [];
  const wasCollapsed = _collapsedGroups(cart).has(key);
  // Beide Merker weg, dann den neuen setzen — sonst hätte ein Gast, der
  // zweimal umentscheidet, zwei widersprüchliche Einträge in der Liste.
  _ui.groups = _ui.groups.filter(x => x !== '+' + key && x !== '-' + key);
  _ui.groups.push((wasCollapsed ? '+' : '-') + key);
  _saveUi();
  _renderSheet();
}

/** Die Details einer Position auf/zu. */
export function toggleCartLine(lineId) {
  const id = String(lineId || '');
  if (!id) return;
  _ui.lines = _ui.lines.includes(id) ? _ui.lines.filter(x => x !== id) : _ui.lines.concat(id);
  _saveUi();
  _renderSheet();
}

export function closeTableCartSheet() {
  _sheetOpen = false;
  _sheetMode = 'cart';
  _payOptions = null;
  _receiptView = null;
  _receiptHtml = null;
  _receiptHtmlFor = null;
  const sheet = document.getElementById('ct-sheet');
  const back = document.getElementById('ct-sheet-backdrop');
  if (sheet) { sheet.hidden = true; sheet.removeAttribute('data-receipt'); }
  if (back) back.hidden = true;
}

/* --------------------------- Bezahlen (S246 Cut D) ------------------------ */

/** Sheet „Nur meins / Ganze Rechnung" — die Beträge kommen frisch vom Server. */
export async function openTablePaySheet() {
  try {
    _payOptions = await _rpc('guest_pay_options', { p_token: _token, p_guest_key: _guestKey });
  } catch (err) {
    showToast(_friendly(err), 'error');
    return;
  }
  _sheetOpen = true;
  _sheetMode = 'pay';
  _renderSheet();
}

/**
 * Zahlung starten: erst den Teilbeleg halten lassen, dann zu Stripe.
 *
 * Die Reihenfolge ist nicht verhandelbar — ohne die Haltung könnte der Service
 * dieselben Positionen kassieren, während der Gast bei Stripe tippt.
 */
export async function startTablePay(scope) {
  const btns = document.querySelectorAll('.ct-pay-option');
  btns.forEach(b => { b.disabled = true; });
  try {
    const res = await _rpc('prepare_guest_receipt', {
      p_token: _token, p_guest_key: _guestKey, p_scope: scope
    });
    if (res && res.nothing_to_pay) {
      showToast(res.reason === 'nothing_of_yours'
        ? 'Von dir ist gerade nichts offen.'
        : 'Es ist nichts mehr offen.', 'info');
      closeTableCartSheet();
      await refreshSession();
      return;
    }
    await _goToStripe(res.receipt_id);
  } catch (err) {
    showToast(_friendly(err), 'error');
    btns.forEach(b => { b.disabled = false; });
  }
}

/** Der Gast hat den Stripe-Tab zugemacht und tippt erneut — gleiche Haltung. */
export async function resumeTablePay() {
  const hold = _session && _session.order && _session.order.my_hold;
  if (!hold || !hold.receipt_id) { await openTablePaySheet(); return; }
  try {
    // Die Haltung verlängern (idempotent), damit sie während des zweiten
    // Anlaufs nicht mitten im Checkout abläuft.
    const res = await _rpc('prepare_guest_receipt', {
      p_token: _token, p_guest_key: _guestKey, p_scope: hold.scope || 'mine'
    });
    await _goToStripe((res && res.receipt_id) || hold.receipt_id);
  } catch (err) {
    showToast(_friendly(err), 'error');
  }
}

async function _goToStripe(receiptId) {
  const cfg = (window.AppConfig && window.AppConfig.backend) || {};
  const apiBase = cfg.apiBaseUrl || '';
  const anonKey = (cfg.supabase && cfg.supabase.anonKey) || '';
  if (!apiBase || !anonKey) {
    showToast('Online-Bezahlung ist gerade nicht eingerichtet.', 'error');
    return;
  }
  // Zurück auf GENAU diesen Tisch — nicht auf die Startseite. Der Hash bleibt,
  // die Query trägt das Ergebnis.
  const base = window.location.origin + window.location.pathname;
  const hash = '#tisch/' + encodeURIComponent(_token);
  const resp = await fetch(apiBase.replace(/\/$/, '') + '/create-order-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + anonKey,
      'apikey': anonKey
    },
    body: JSON.stringify({
      receipt_id: receiptId,
      return_url: base + '?stripe_return=1' + hash,
      cancel_url: base + '?stripe_cancel=1' + hash
    })
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data.url) {
    console.error('[customer-table] Checkout:', data);
    showToast('Die Zahlung ließ sich nicht starten. Bitte beim Service melden.', 'error');
    return;
  }
  window.location.assign(data.url);
}

/** Drucken. Eigene Funktion statt `window.print()` im Handler: der
 *  dom-bindings-Parser löst nur blanke Funktionsnamen auf (CSP-Regel). */
/**
 * Drucken — das IFRAME, nicht die Seite (S254).
 *
 * Vorher druckte `window.print()` die Bestellseite; eine Druck-Regel im
 * globalen CSS blendete alles ausser dem Sheet aus. Jetzt liegt der Bon in
 * seinem eigenen Dokument und bringt seine eigene `@media print`-Regel mit —
 * gedruckt wird genau er.
 */
export function printGuestReceipt() {
  const frame = document.getElementById('ct-receipt-frame');
  const win = frame && frame.contentWindow;
  if (win && typeof win.print === 'function') {
    try { win.focus(); } catch (_) { /* manche Browser verweigern focus() */ }
    win.print();
    return;
  }
  // Kein iframe (alter Stand, blockierte Sandbox) → lieber die Seite drucken
  // als gar nichts.
  if (typeof window.print === 'function') window.print();
}

/** „📄 Beleg" — Inhalt kommt vom Server, inklusive TSE-Daten und QR. */
export async function openGuestReceipt(receiptId) {
  _sheetOpen = true;
  _sheetMode = 'receipt';
  _receiptView = null;
  _receiptHtml = null;
  _receiptHtmlFor = null;
  const sheet = document.getElementById('ct-sheet');
  if (sheet) sheet.removeAttribute('data-receipt');
  _renderSheet();
  try {
    _receiptView = await _rpc('guest_receipt_view', {
      p_token: _token, p_guest_key: _guestKey, p_receipt_id: receiptId
    });
    try {
      _receiptHtml = await buildStandaloneReceiptHtml(
        _receiptOrderFromView(_receiptView),
        {
          company: _receiptCompanyFromView(_receiptView),
          // S255 (B2) — der Rückfall wird HIER entschieden, nicht per
          // `onerror` im Dokument: das iframe läuft ohne `allow-scripts`
          // (Isolation seit S230), ein Inline-Handler käme dort nie zum Zug und
          // stünde nur als „Blocked script execution" in der Gast-Konsole.
          logoSrc: _receiptLogoFromView(_receiptView) || DEFAULT_LOGO_URL,
          noInlineScripts: true
        }
      );
      _receiptHtmlFor = String(_receiptView.receipt_id || '');
    } catch (buildErr) {
      // Der Beleg ist da, nur das Dokument nicht — das ist ein Fehler in
      // UNSEREM Renderer, nicht in den Daten. Sichtbar machen, statt dem Gast
      // ein leeres Sheet zu zeigen.
      console.error('[customer-table] Beleg-Dokument nicht baubar:', buildErr);
      showToast('Der Beleg lässt sich gerade nicht anzeigen. Bitte kurz beim Service melden.', 'error');
      closeTableCartSheet();
      return;
    }
    if (sheet) sheet.removeAttribute('data-receipt');
    _renderSheet();
  } catch (err) {
    console.warn('[customer-table] guest_receipt_view:',
      (err && (err.code || '')) || '', (err && (err.message || err.hint)) || err);
    showToast(_friendly(err), 'error');
    closeTableCartSheet();
  }
}

/* --------------------------- Küchen-Hinweis (S253) ------------------------ */

const NOTE_DEBOUNCE_MS = 600;

/**
 * Speichern — verzögert beim Tippen, sofort beim Verlassen des Feldes.
 *
 * Der Blur-Weg ist nicht Kosmetik: wer den letzten Buchstaben tippt und sofort
 * auf „Absenden" geht, verlöre ihn sonst, weil der Timer nie ablief (Memory
 * `feedback_gg_template_autosave_trailing_race`).
 */
async function _saveGuestNote(value) {
  const v = String(value == null ? '' : value).trim();
  if (_noteSaved === v) return;              // nichts Neues zu melden
  _noteSaved = v;
  try {
    await _rpc('set_table_guest_note', {
      p_token: _token, p_guest_key: _guestKey, p_note: v
    });
    await refreshSession();
  } catch (err) {
    _noteSaved = null;                       // beim nächsten Versuch neu senden
    console.warn('[customer-table] set_table_guest_note:', err && (err.code || ''), err && err.message);
    showToast(_friendly(err), 'error');
  }
}

/** `oninput` — Zähler sofort, Speichern verzögert. */
export function tableNoteInput(value) {
  const counter = document.getElementById('ct-note-count');
  if (counter) {
    const next = noteCounterText(value);
    if (counter.textContent !== next) counter.textContent = next;
  }
  clearTimeout(_noteTimer);
  _noteTimer = setTimeout(() => { _noteTimer = null; _saveGuestNote(value); }, NOTE_DEBOUNCE_MS);
}

/** `onblur` — den wartenden Timer abräumen und sofort speichern. */
export function tableNoteBlur(value) {
  clearTimeout(_noteTimer);
  _noteTimer = null;
  return _saveGuestNote(value);
}

/** Hook aus dem Menü-Picker: bestätigte Zeile sofort in den Tisch-Warenkorb. */
export async function addLineToTableCart(request) {
  if (!request) return;
  try {
    await _rpc('table_cart_add', {
      p_token: _token, p_guest_key: _guestKey,
      p_guest_name: _guestName || null, p_item: request
    });
    showToast('Zum Tisch-Warenkorb hinzugefügt.', 'success');
    await refreshSession();
  } catch (err) {
    showToast(_friendly(err), 'error');
  }
}

export async function tableCartSetQty(cartItemId, qty) {
  try {
    await _rpc('table_cart_set_qty', {
      p_token: _token, p_guest_key: _guestKey, p_cart_item_id: cartItemId, p_qty: qty
    });
    await refreshSession();
  } catch (err) { showToast(_friendly(err), 'error'); }
}

export async function tableCartRemove(cartItemId) {
  try {
    await _rpc('table_cart_remove', {
      p_token: _token, p_guest_key: _guestKey, p_cart_item_id: cartItemId
    });
    await refreshSession();
  } catch (err) { showToast(_friendly(err), 'error'); }
}

/**
 * Service rufen bzw. um die Rechnung bitten.
 *
 * Ein zweiter Druck ist ausdrücklich erlaubt: der Server antwortet dann
 * `already_open` statt mit einem Fehler. Wer nervös zweimal tippt, weil er
 * nicht sicher ist, ob der erste Druck ankam, soll nicht abgewatscht werden.
 */
export async function callTableService(kind) {
  try {
    const res = await _rpc('table_call_service', {
      p_token: _token, p_guest_key: _guestKey, p_kind: kind
    });
    if (res && res.already_open) {
      showToast(kind === 'call_pay'
        ? 'Die Rechnung ist schon angefragt.'
        : 'Der Service ist schon unterwegs.', 'info');
    } else {
      showToast(kind === 'call_pay'
        ? 'Wir bringen gleich die Rechnung.'
        : 'Der Service kommt gleich vorbei.', 'success');
    }
    await refreshSession();
  } catch (err) {
    showToast(_friendly(err), 'error');
  }
}

/**
 * Absenden — und das gibt es seit S251 nur noch hier, im Warenkorb.
 *
 * `submit_table_cart` schickt IMMER den ganzen Warenkorb der Belegung als eine
 * Runde (Advisory-Lock je Belegung, `round_no = max + 1`). Das ist so gewollt —
 * „der Tisch bestellt gemeinsam" —, aber es heisst auch: wer hier tippt, schickt
 * unter Umständen die halbfertige Auswahl der anderen mit. Deshalb die
 * Rückfrage, und zwar NUR dann, mit Namen und Beträgen. Wer allein seine eigenen
 * Zeilen abschickt, soll nicht jedes Mal einen Dialog wegtippen.
 */
export async function submitTableCart() {
  const cart = (_session && _session.cart) || [];
  const s = cartSummary(cart);
  if (!s.lines) { showToast('Der Warenkorb ist leer.', 'info'); return; }
  const table = (_session && _session.table_name) || '';
  const round = nextRoundNo(_session && _session.order);
  const foreign = submitConfirmBody(cart);

  if (foreign) {
    const ok = await window.styledConfirm({
      title: `Runde ${round} für Tisch ${table} absenden?`,
      body: `${s.lines === 1 ? '1 Position' : s.lines + ' Positionen'} · `
          + `${fmtEUR(s.totalCents / 100)}. ${foreign}. Die Küche legt danach los.`,
      confirmLabel: 'Absenden'
    });
    if (!ok.confirmed) return;
  }

  const btn = document.querySelector('.ct-submit');
  const label = btn ? btn.innerHTML : '';
  if (btn) { btn.disabled = true; btn.textContent = 'Wird gesendet …'; }
  try {
    const res = await _rpc('submit_table_cart', { p_token: _token, p_guest_key: _guestKey });
    if (res && res.nothing_to_submit) {
      // Zwei Handys haben gleichzeitig getippt — der Server hat genau eine Runde
      // daraus gemacht, und dieses Gerät kam als zweites. Kein Fehler: die
      // Bestellung IST raus.
      showToast('Der Tisch hat gerade abgeschickt — deine Positionen sind mit dabei.', 'info');
    } else {
      showToast('Bestellung ist in der Küche!', 'success');
    }
    // Ab jetzt ist die Karte im Weg: der Gast will seinen Status sehen. Er kann
    // sie mit „➕ Nachbestellen" jederzeit wieder aufklappen.
    _ui.menu = true;
    _saveUi();
    closeTableCartSheet();
    await refreshSession();
  } catch (err) {
    // S253 (Beifang aus dem S252-Retest): der Gast sieht bewusst nur einen
    // freundlichen Satz — die Konsole aber schwieg bisher völlig. Wer den Fall
    // nachstellen muss, hat ohne Code und Meldung nichts in der Hand (im Retest
    // war es eine FK-Verletzung 23503, sichtbar erst nach Nachbau der RPC).
    console.warn('[customer-table] submit_table_cart:',
      (err && (err.code || err.status || '')) || '', (err && (err.message || err.hint)) || err);
    showToast(_friendly(err), 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = label; }
  }
}

/** Server-Fehler in Gast-Sprache. Die technischen Präfixe (`TABLE_LOCKED:` …)
 *  sind für das Log gedacht, nicht für jemanden, der am Tisch sitzt. */
function _friendly(err) {
  const msg = String((err && (err.message || err.hint)) || '');
  if (/TABLE_LOCKED/.test(msg))        return 'Der Tisch ist gerade nicht freigegeben — bitte kurz beim Service melden.';
  if (/RATE_LIMIT/.test(msg))          return 'Einen Moment bitte, das war zu schnell hintereinander.';
  if (/CART_ITEM_NOT_YOURS/.test(msg)) return 'Diese Position gehört jemand anderem am Tisch.';
  if (/ITEM_UNAVAILABLE/.test(msg))    return 'Das ist gerade leider aus.';
  if (/ITEM_UNKNOWN|OPTION_UNKNOWN/.test(msg)) return 'Das gibt es so nicht mehr — bitte die Karte neu laden.';
  if (/DEAL_/.test(msg))               return 'Diese Aktion ist gerade nicht verfügbar.';
  if (/CART_FULL/.test(msg))           return 'Der Tisch-Warenkorb ist voll — bitte erst absenden.';
  if (/NO_OCCUPANCY/.test(msg))        return 'An diesem Tisch ist gerade niemand eingetragen — bitte kurz beim Service melden.';
  if (/SIGNAL_KIND_INVALID|SCOPE_INVALID/.test(msg)) return 'Das hat gerade nicht geklappt. Bitte die Seite neu laden.';
  if (/RECEIPT_NOT_FOUND/.test(msg))   return 'Diesen Beleg gibt es hier nicht.';
  if (/NOTE_TOO_LONG/.test(msg))       return 'Der Hinweis ist zu lang — bitte auf 200 Zeichen kürzen.';
  return 'Das hat gerade nicht geklappt. Bitte nochmal versuchen.';
}

/* -------------------------------------------------------------------------- */
if (typeof window !== 'undefined') {
  Object.assign(window, {
    checkTableRequestFromUrl,
    renderTableView,
    setTableGuestName,
    editTableGuestName,
    openTableCartSheet,
    closeTableCartSheet,
    tableCartSetQty,
    tableCartRemove,
    submitTableCart,
    // S251 Cut UX
    toggleTableMenu,
    toggleCartGroup,
    toggleCartLine,
    // S253 — Küchen-Hinweis
    tableNoteInput,
    tableNoteBlur,
    // S245 Cut C
    callTableService,
    // S246 Cut D
    openTablePaySheet,
    startTablePay,
    resumeTablePay,
    openGuestReceipt,
    printGuestReceipt
  });
}
